import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaCheck, FaTimes } from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  Badge,
  PrimaryButton,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import {
  DEFAULT_PAYOUT_PLANNING,
  EASY_PLACES,
  RECOMMENDED_PAYOUT_OPTIONS,
  buildPayoutTableFromRecipe,
  isColumnPercentValid,
  isPayoutPlanningEmpty,
  isPayoutPlanningPartial,
  isRecommendedPayoutOptions,
  payoutTablesMatch,
  recipeForLoadedTable,
  recipeToPayoutTableColumns,
} from "../../../utility/generateShapedPayoutTable";
import {
  deletePayoutTableFamily,
  fetchDefaultPayoutTableId,
  loadPayoutTable,
  rootPayoutTableId,
  savePayoutTable,
  setOrganizationDefaultPayoutTable,
  tableIsOrgDefault,
} from "./useOrgPayoutTables";
import PayoutTableWorkspace from "./PayoutTableWorkspace";
import { RoundWholeDollarsSwitch } from "./PayoutOptionsPanel";
import DeleteWithConfirmation from "../../PayoutTable/components/DeleteWithConfirmation";

const inputClass =
  "w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink";

const OrganizationPayoutTableEditor = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { canManageTournaments, canManageOrganization } = usePermissions();
  const canEdit = canManageTournaments || canManageOrganization;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [table, setTable] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [payoutData, setPayoutData] = useState([]);
  const [original, setOriginal] = useState({
    name: "",
    description: "",
    payoutData: [],
    advanced: { ...RECOMMENDED_PAYOUT_OPTIONS },
    planning: { ...DEFAULT_PAYOUT_PLANNING },
    cellsEdited: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [advanced, setAdvanced] = useState({ ...RECOMMENDED_PAYOUT_OPTIONS });
  const [planning, setPlanning] = useState({ ...DEFAULT_PAYOUT_PLANNING });
  const [cellsEdited, setCellsEdited] = useState(false);
  const [defaultPayoutTableId, setDefaultPayoutTableId] = useState(null);
  const [settingDefault, setSettingDefault] = useState(false);

  const load = async (id) => {
    setLoading(true);
    setError("");
    try {
      const result = await loadPayoutTable(id);
      if (result.table.frozenForTournamentId) {
        setError(
          "This is a copy taken when a tournament locked payouts. Open it from that tournament."
        );
        setTable(null);
        return;
      }
      if (
        currentOrganization?.id &&
        result.table.organizationId !== currentOrganization.id
      ) {
        setError("This table is not in the current organization.");
        setTable(null);
        return;
      }
      setTable(result.table);
      setName(result.table.name || "");
      setDescription(result.table.description || "");
      const recipe = recipeForLoadedTable(result.table, result.payoutData);
      setAdvanced(recipe.options);
      setPlanning(recipe.planning);
      setCellsEdited(recipe.cellsEdited);
      setIsEditing(false);

      if (currentOrganization?.id) {
        setDefaultPayoutTableId(
          await fetchDefaultPayoutTableId(currentOrganization.id)
        );
      }

      const snapshot = {
        name: result.table.name || "",
        description: result.table.description || "",
        advanced: recipe.options,
        planning: recipe.planning,
        cellsEdited: recipe.cellsEdited,
      };

      if (result.payoutData.length > 0) {
        setPayoutData(result.payoutData);
        setOriginal({ ...snapshot, payoutData: result.payoutData });
      } else {
        setPayoutData(buildPayoutTableFromRecipe(recipe.options, recipe.planning));
        setOriginal({ ...snapshot, payoutData: [] });
      }
    } catch (err) {
      console.error(err);
      setError("Could not load this payout table.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tableId) load(tableId);
  }, [tableId]);

  const dirty = useMemo(() => {
    return (
      name !== original.name ||
      description !== original.description ||
      JSON.stringify(payoutData) !== JSON.stringify(original.payoutData) ||
      JSON.stringify(advanced) !== JSON.stringify(original.advanced) ||
      JSON.stringify(planning) !== JSON.stringify(original.planning) ||
      cellsEdited !== original.cellsEdited
    );
  }, [name, description, payoutData, advanced, planning, cellsEdited, original]);

  const isRecommended = useMemo(() => {
    if (cellsEdited) return false;
    if (!isPayoutPlanningEmpty(planning)) return false;
    if (!isRecommendedPayoutOptions(advanced)) return false;
    return payoutTablesMatch(
      payoutData,
      buildPayoutTableFromRecipe(
        RECOMMENDED_PAYOUT_OPTIONS,
        DEFAULT_PAYOUT_PLANNING
      )
    );
  }, [advanced, payoutData, planning, cellsEdited]);

  const isDefault = tableIsOrgDefault(table, defaultPayoutTableId);

  const handleSetDefault = async () => {
    if (!canEdit || !table || !currentOrganization?.id || isDefault) return;
    setSettingDefault(true);
    setError("");
    try {
      const tableIdToSave = rootPayoutTableId(table);
      await setOrganizationDefaultPayoutTable(
        currentOrganization.id,
        tableIdToSave
      );
      setDefaultPayoutTableId(tableIdToSave);
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not set the default table.");
    } finally {
      setSettingDefault(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit || !user || !table || isDefault) return;
    setError("");
    try {
      await deletePayoutTableFamily({
        baseId: rootPayoutTableId(table),
        userId: user.id,
      });
      navigate("/organization/payout-tables");
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not delete this payout table.");
    }
  };

  const optionsLocked = canEdit && cellsEdited;

  const calculateColumnTotal = (quantity) =>
    payoutData
      .filter(
        (row) =>
          row.payoutQuantity === parseInt(quantity, 10) &&
          row.payoutOrder <= parseInt(quantity, 10)
      )
      .reduce((sum, row) => sum + row.percentage, 0);

  const isColumnValid = (quantity) =>
    isColumnPercentValid(calculateColumnTotal(quantity));

  const allColumnsValid =
    payoutData.length === 0 ||
    [...new Set(payoutData.map((row) => row.payoutQuantity))].every(
      (quantity) => isColumnValid(quantity)
    );

  const handlePercentageChange = (quantity, order, value) => {
    if (!canEdit) return;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (
      value !== "" &&
      (Number.isNaN(parseFloat(value)) ||
        parseFloat(value) < 0 ||
        parseFloat(value) > 100)
    ) {
      return;
    }
    const nextPercentage =
      value === ""
        ? 0
        : Math.round((parseFloat(value) / 100) * 10000) / 10000;
    const existing = payoutData.find(
      (row) => row.payoutQuantity === quantity && row.payoutOrder === order
    );
    if (existing && existing.percentage === nextPercentage) return;
    setCellsEdited(true);
    setPayoutData((current) =>
      current.map((row) =>
        row.payoutQuantity === quantity && row.payoutOrder === order
          ? { ...row, percentage: nextPercentage }
          : row
      )
    );
  };

  const handleKeyDown = () => {};

  const handleRoundWholeDollars = (checked) => {
    if (!canEdit) return;
    setPlanning((current) => ({
      ...current,
      roundToWholeDollars: Boolean(checked),
    }));
  };

  const handlePlanningField = (field, value) => {
    if (cellsEdited) return;
    const next = { ...planning, [field]: value };
    setPlanning(next);
    if (isPayoutPlanningPartial(next)) return;
    setPayoutData(buildPayoutTableFromRecipe(advanced, next));
  };

  const handleAdvancedField = (field, value) => {
    if (cellsEdited) return;
    const next = { ...advanced, [field]: value };
    setAdvanced(next);
    setPayoutData(buildPayoutTableFromRecipe(next, planning));
  };

  const handleResetRecommended = () => {
    const next = { ...RECOMMENDED_PAYOUT_OPTIONS };
    const emptyPlanning = { ...DEFAULT_PAYOUT_PLANNING };
    setAdvanced(next);
    setPlanning(emptyPlanning);
    setPayoutData(buildPayoutTableFromRecipe(next, emptyPlanning));
    setIsEditing(false);
    setCellsEdited(false);
  };

  const handleAddColumn = () => {
    const quantities = payoutData.map((row) => row.payoutQuantity);
    const newQuantity = quantities.length ? Math.max(...quantities) + 1 : 1;
    const maxRows = Math.max(
      newQuantity,
      ...payoutData.map((row) => row.payoutOrder),
      1
    );
    const extra = [];
    for (let order = 1; order <= maxRows; order += 1) {
      extra.push({
        payoutQuantity: newQuantity,
        percentage: 0,
        payoutOrder: order,
      });
    }
    setPayoutData([...payoutData, ...extra]);
    setCellsEdited(true);
  };

  const handleDeleteColumn = (quantity) => {
    setPayoutData(payoutData.filter((row) => row.payoutQuantity !== quantity));
    setCellsEdited(true);
  };

  const handleSave = async () => {
    if (!user || !table) return;
    if (dirty && !allColumnsValid) return;
    if (!dirty) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const nextDescription =
        description.trim() || `Recommended table, up to ${EASY_PLACES} places.`;
      const saved = await savePayoutTable({
        currentTable: table,
        name: name.trim() || "Payout table",
        description: nextDescription,
        payoutData,
        recipe: recipeToPayoutTableColumns({
          options: advanced,
          planning,
          cellsEdited,
        }),
      });
      await load(saved.id);
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not save this payout table.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setName(original.name);
    setDescription(original.description);
    setPayoutData(
      original.payoutData.length > 0
        ? original.payoutData
        : buildPayoutTableFromRecipe(original.advanced, original.planning)
    );
    setAdvanced(original.advanced);
    setPlanning(original.planning);
    setCellsEdited(original.cellsEdited);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="space-y-4" role="status" aria-label="Loading payout table">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={8} />
      </div>
    );
  }

  if (!table) {
    return (
      <div className="space-y-4">
        <Link
          to="/organization/payout-tables"
          className="inline-flex items-center gap-2 text-sm text-brand-charcoal hover:underline"
        >
          <FaArrowLeft className="h-3 w-3" />
          All payout tables
        </Link>
        <InlineAlert
          type="error"
          title="There was a problem"
          message={error || "Could not load this payout table."}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 min-w-0">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <Link
            to="/organization/payout-tables"
            className="inline-flex items-center gap-2 text-sm text-brand-mist hover:text-brand-cream mb-2"
          >
            <FaArrowLeft className="h-3 w-3" />
            All payout tables
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              {name || "Payout table"}
            </h1>
            {isDefault ? (
              <Badge className="bg-brand-gold text-brand-charcoal">
                Default
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-brand-mist mt-1">
            {isDefault
              ? "New tournaments start on this table. Changing that later does not move events already created."
              : isRecommended
                ? "Recommended split. Open more options to see how it was built."
                : "Custom split. Reset to recommended to get the standard 30-place, 1 in 6 table."}
          </p>
        </div>
        <div className="p-4 sm:p-6 flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1 space-y-3">
            <div>
              <label className="block text-sm font-medium text-brand-charcoal mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-charcoal mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canEdit}
                rows={2}
                className={inputClass}
                placeholder="Optional. Example: Saturday sweeper, 1 in 6."
              />
            </div>
            <RoundWholeDollarsSwitch
              checked={planning.roundToWholeDollars !== false}
              onChange={handleRoundWholeDollars}
              disabled={!canEdit}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && !isDefault ? (
              <SecondaryButton
                onClick={handleSetDefault}
                disabled={settingDefault}
                className="!px-3 !py-2 text-sm"
              >
                {settingDefault ? "Saving..." : "Use as default"}
              </SecondaryButton>
            ) : null}
            {canEdit && (dirty || isEditing) ? (
              <>
                <PrimaryButton
                  onClick={handleSave}
                  icon={FaCheck}
                  disabled={saving || (dirty && !allColumnsValid)}
                >
                  {saving ? "Saving..." : "Save"}
                </PrimaryButton>
                {original.payoutData.length > 0 || isEditing ? (
                  <SecondaryButton
                    onClick={handleCancel}
                    icon={FaTimes}
                    className="!px-3 !py-2 text-sm"
                  >
                    Cancel
                  </SecondaryButton>
                ) : null}
              </>
            ) : null}
            {canEdit && isDefault ? (
              <p className="text-sm text-brand-muted max-w-xs">
                Pick another default before deleting this table.
              </p>
            ) : null}
            {canEdit && !isDefault ? (
              <DeleteWithConfirmation
                onDelete={handleDelete}
                isLoggedIn={!!user}
                title="Delete payout table"
                message="Delete this table? Unlocked events using it will have no table until you pick another."
              />
            ) : null}
          </div>
        </div>
      </header>

      {error ? (
        <InlineAlert type="error" title="There was a problem" message={error} />
      ) : null}
      {canEdit && dirty && !allColumnsValid ? (
        <InlineAlert
          type="warning"
          title="Columns must total 100%"
          message="Every places-paid column has to add to 100% before you can save."
        />
      ) : null}

      <PayoutTableWorkspace
        canEdit={canEdit}
        showMore={showMore}
        onToggleMore={() => setShowMore((open) => !open)}
        places={advanced.places}
        shape={advanced.shape}
        ratioNumerator={advanced.ratioNumerator}
        ratioDenominator={advanced.ratioDenominator}
        payoutData={payoutData}
        planning={planning}
        onPlanningChange={handlePlanningField}
        isEditing={isEditing}
        onChangeField={handleAdvancedField}
        isRecommended={isRecommended}
        onResetRecommended={handleResetRecommended}
        optionsLocked={optionsLocked}
        onEditCells={() => {
          setShowMore(true);
          setIsEditing(true);
        }}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        canSave={!dirty || allColumnsValid}
        calculateColumnTotal={calculateColumnTotal}
        isColumnValid={isColumnValid}
        onPercentageChange={handlePercentageChange}
        onKeyDown={handleKeyDown}
        onAddColumn={handleAddColumn}
        onDeleteColumn={handleDeleteColumn}
      />
    </div>
  );
};

export default OrganizationPayoutTableEditor;
